import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Cards } from "../components/Cards.tsx";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import toast from "react-hot-toast";
import Spinner from "../components/PageLoader.js";
import { getStaffShifts } from "../services/shifts.js";
import { getServiceUserPRNs } from "../services/users.js";
import {
  capitalize,
  formatUKTime,
  formatUTCForDB,
  convertLocalToUTC,
} from "../utils/functions.js";
import { MedicationAdministrationModal } from "../components/modals/MedicationAdministrationModal.js";
import EmptyState from "../components/EmptyState.js";
import {
  administerPRNMedication,
  getAdministeredPRNMedications,
  upsertAdministeredPRNMedication,
} from "../services/medication.js";

const validationSchema = Yup.object().shape({
  selectedMedications: Yup.array()
    .min(1, "Please select at least one medication")
    .required("Please select at least one medication"),
  administrationType: Yup.string().required("Administration type is required"),
  signature: Yup.string().required("Signature is required"),
  notes: Yup.string().required("Notes are required"),
});

const defaultValues = {
  selectedMedications: [],
  administrationType: "",
  outcome: "",
  signature: "",
  notes: "",
};

const FieldShiftPRNComponent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [prnMedications, setPrnMedications] = useState([]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [shift, setShift] = useState(null);
  const [showAdministrationModal, setShowAdministrationModal] = useState(false);
  const [administeredPRNMedication, setAdministeredPRNMedication] = useState(
    []
  );
  const navigate = useNavigate();

  const user = useMemo(() => JSON.parse(localStorage.getItem("user")), []);
  const urlParam = useParams();
  const shiftId = urlParam.id;

  const serviceUserId = urlParam.serviceUserId;

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const fetchShift = async () => {
    try {
      const { data } = await getStaffShifts({ userId: user.username || "" });
      const currentShift = data.find(
        (shift) => shift.shiftId === Number(shiftId)
      );
      setShift(currentShift);
    } catch (error) {
      console.error("Error fetching shift:", error);
      toast.error("Failed to fetch shift details");
    }
  };

  const fetchPRNMedications = async () => {
    setIsLoading(true);
    try {
      const { data } = await getServiceUserPRNs(serviceUserId);
      setPrnMedications(data);
    } catch (error) {
      console.error("Error fetching PRN medication:", error);
      toast.error("Failed to fetch PRN medication");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdministeredPRNMedications = async () => {
    try {
      const { data } = await getAdministeredPRNMedications(shiftId,serviceUserId);

      const administeredData = Array.isArray(data) ? data : (data?.administeredPrns || []);
      setAdministeredPRNMedication(administeredData);
    } catch (error) {
      console.error("Error fetching PRN administered medications:", error);
      setAdministeredPRNMedication([]);
    }
  };

  useEffect(() => {
    if (shiftId && serviceUserId) {
      fetchShift();
      fetchPRNMedications();
      fetchAdministeredPRNMedications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMedicationSelect = (medicationId) => {
    const isSelected = selectedMedications.includes(medicationId);
    let newSelection;

    if (isSelected) {
      newSelection = selectedMedications.filter((id) => id !== medicationId);
    } else {
      newSelection = [...selectedMedications, medicationId];
    }

    setSelectedMedications(newSelection);
    setValue("selectedMedications", newSelection);
  };

  const handleSelectAll = () => {
    // Only select medications that haven't been administered yet
    const availableMedications = prnMedications.filter(
      (med) => !isAdministered(med.id)
    );

    if (selectedMedications.length === availableMedications.length) {
      setSelectedMedications([]);
      setValue("selectedMedications", []);
    } else {
      const availableMedIds = availableMedications.map((med) => med.id);
      setSelectedMedications(availableMedIds);
      setValue("selectedMedications", availableMedIds);
    }
  };

  const isAdministered = (medicationId) => {
    return administeredPRNMedication.some((admin) => {
      const adminId = admin.medication?.id || admin.medicationId || admin.id;
      return adminId === medicationId || adminId == medicationId || String(adminId) === String(medicationId); // eslint-disable-line eqeqeq
    });
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    if (administeredPRNMedication.length > 0) {
      try {
        const selectedMeds = prnMedications.filter((med) =>
          data.selectedMedications.includes(med.id)
        );
        for (const medication of selectedMeds) {
          const upsertPayload = {
            shiftId: shift.shiftId,
            serviceUserId: serviceUserId,
            medicationItemId: medication.id,
            administrationDetails: {
              administrationType: data.administrationType,
              signature: data.signature,
              notes: data.notes,
              timestamp: formatUTCForDB(
                convertLocalToUTC(new Date()),
                "yyyy-MM-dd HH:mm:ss"
              ),
              administeredBy: user.username,
            },
          };

          await upsertAdministeredPRNMedication(upsertPayload);
        }
        await fetchAdministeredPRNMedications();
        setShowAdministrationModal(false);

        const remainingSelected = selectedMedications.filter(
          (medId) => !selectedMeds.some((med) => med.id === medId)
        );
        setSelectedMedications(remainingSelected);
        setValue("selectedMedications", remainingSelected);

        toast.success("Prn Medication administered successfully");
      } catch (error) {
        console.error("Error submitting PRN medication administration:", error);
        toast.error("Failed to record PRN medication administration");
      } finally {
        setIsLoading(false);
      }
    } else {
      try {
        const selectedMeds = prnMedications.filter((med) =>
          data.selectedMedications.includes(med.id)
        );

        // Create new administration records for selected medications
        const newAdministrationRecords = selectedMeds.map((med) => ({
          medication: {
            id: med.id,
            name: med.name,
            dose: med.dose,
            quantity: med.quantity,
            route: med.route,
            instructions: med.instructions,
            form: med.form,
          },
          administrationDetails: {
            administrationType: data.administrationType,
            signature: data.signature,
            notes: data.notes,
            timestamp: formatUTCForDB(
              convertLocalToUTC(new Date()),
              "yyyy-MM-dd HH:mm:ss"
            ),
            administeredBy: user.username,
          },
        }));

        // Create the new JSON structure
        const administrationPayload = {
          shiftId: shift.shiftId.toString(),
          serviceUserId: serviceUserId,
          administeredPrns: newAdministrationRecords,
        };

        await administerPRNMedication(administrationPayload);
        await fetchAdministeredPRNMedications();
        setShowAdministrationModal(false);

        // Clear selection for administered medications
        const remainingSelected = selectedMedications.filter(
          (medId) => !selectedMeds.some((med) => med.id === medId)
        );
        setSelectedMedications(remainingSelected);
        setValue("selectedMedications", remainingSelected);

        toast.success("PRN administered successfully");
      } catch (error) {
        console.error("Error submitting PRN administration:", error);
        toast.error("Failed to record PRN administration");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      {isLoading && <Spinner />}

      {!isLoading && prnMedications.length > 0 && (
        <div className="field-shift__prn">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Medication Selection */}
            <Cards classes="mb-24">
              <div className="field-shift__prn-header">
                <h5 className="mb-16">PRN Medication</h5>
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={handleSelectAll}
                >
                  {(() => {
                    const availableMedications = prnMedications.filter(
                      (med) => !isAdministered(med.id)
                    );
                    return selectedMedications.length === availableMedications.length
                      ? "Deselect All"
                      : "Select All";
                  })()}
                </button>
              </div>

              {errors.selectedMedications && (
                <div className="form__error mb-16">
                  {errors.selectedMedications.message}
                </div>
              )}

              <div className="field-shift__prn-list">
                {prnMedications.map((medication) => (
                  <div
                    key={medication.id}
                    className={`field-shift__prn-item ${
                      isAdministered(medication.id) ? "administered" : ""
                    }`}
                  >
                    <div className="custom-checkbox">
                      <input
                        type="checkbox"
                        id={`med-${medication.id}`}
                        className="custom-checkbox__input"
                        checked={selectedMedications.includes(medication.id)}
                        onChange={() => handleMedicationSelect(medication.id)}
                        disabled={isAdministered(medication.id)}
                      />
                      <label
                        htmlFor={`med-${medication.id}`}
                        className="custom-checkbox__label"
                      >
                        <div className="custom-checkbox__box">
                          {selectedMedications.includes(medication.id) && (
                            <svg
                              className="custom-checkbox__icon"
                              viewBox="0 0 16 16"
                              fill="white"
                            >
                              <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                            </svg>
                          )}
                        </div>
                      </label>
                    </div>

                    <div className="field-shift__prn-details">
                      <div className="field-shift__prn-name">
                        {medication.name}

                        {isAdministered(medication.id) && (
                          <span className="field-shift__administered-badge">
                            {" - "}Administered
                          </span>
                        )}
                      </div>
                      <div className="field-shift__prn-info">
                        <span className="field-shift__prn-dose">
                          {medication.dose}
                        </span>
                        <span className="field-shift__prn-quantity">
                          • {medication.quantity}
                        </span>
                        <span className="field-shift__prn-route">
                          • {capitalize(medication.route)}
                        </span>
                      </div>
                      {medication.instructions && (
                        <div className="field-shift__prn-instructions">
                          {medication.instructions}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Cards>

          </form>
        </div>
      )}

      {/* Floating Action Button */}
      {selectedMedications.length > 0 && (
        <div className="field-shift__prn-floating-action">
          <button
            type="button"
            className="btn btn__primary btn--floating"
            onClick={() => setShowAdministrationModal(true)}
          >
            Administer {selectedMedications.length} medication
            {selectedMedications.length > 1 ? "s" : ""}
          </button>
        </div>
      )}

      {/* Administration History - Always show for audit purposes, even if PRNs are deleted */}
      {administeredPRNMedication && administeredPRNMedication.length > 0 && (
        <Cards classes="mb-24">
          <h5 className="mb-16">Administration History</h5>
          <div className="field-shift__administered-list">
            {administeredPRNMedication.map((admin, index) => {
              // Handle deleted PRN medications - preserve audit trail
              const medicationName = admin.medication?.name ||
                                   admin.medicationName ||
                                   `[Deleted PRN ID: ${admin.medication?.id || 'Unknown'}]`;

              const isDeletedPRN = !admin.medication?.name && !admin.medicationName;

              return (
                <div key={index} className="field-shift__administered-item">
                  <div>
                    <span
                      className="field-shift__administered-name"
                      style={isDeletedPRN ? {
                        opacity: 0.7,
                        fontStyle: 'italic',
                        color: 'var(--text-secondary)'
                      } : {}}
                    >
                      {medicationName}
                      {isDeletedPRN && (
                        <span
                          style={{
                            marginLeft: '8px',
                            fontSize: '12px',
                            color: 'var(--status-warning)',
                            backgroundColor: 'var(--status-warning-bg, #fff3cd)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid var(--status-warning)',
                            fontWeight: 'normal'
                          }}
                          title="This PRN medication has been deleted but administration record is preserved for audit purposes"
                        >
                          Deleted
                        </span>
                      )}
                    </span>
                    <div className="field-shift__administered-details">
                      <p>
                        Type:{" "}
                        {admin.administrationDetails?.administrationType ||
                          admin.administrationType}
                      </p>
                      <p>
                        By:{" "}
                        {admin.administrationDetails?.signature ||
                          admin.signature}
                      </p>
                      <p>
                        Time:{" "}
                        {formatUKTime(
                          admin.administrationDetails?.timestamp ||
                            admin.timestamp,
                          "dd/MM/yyyy HH:mm"
                        )}
                      </p>
                    </div>
                    {(admin.administrationDetails?.notes || admin.notes) && (
                      <div className="field-shift__administered-notes">
                        {admin.administrationDetails?.notes || admin.notes}
                      </div>
                    )}
                  </div>
                  <span className="field-shift__administered-status">
                    Administered
                  </span>
                </div>
              );
            })}
          </div>
        </Cards>
      )}

      {/* Medication Administration Modal */}
      {showAdministrationModal && (
        <MedicationAdministrationModal
          isOpen={showAdministrationModal}
          onClose={() => setShowAdministrationModal(false)}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isLoading={isLoading}
          selectedCount={selectedMedications.length}
          medicationType="PRN Medication"
        />
      )}

      {!isLoading && prnMedications.length === 0 && (!administeredPRNMedication || administeredPRNMedication.length === 0) && (
        <EmptyState
          title="No PRN Medication Available"
          description="There are no PRN medications configured for this service user."
          actionButton={
            <button
              className="btn btn__secondary mt-16"
              onClick={() =>
                navigate(`/field-shifts/${shiftId}/${serviceUserId}`)
              }
            >
              Go Back
            </button>
          }
        />
      )}

      {!isLoading && prnMedications.length === 0 && administeredPRNMedication && administeredPRNMedication.length > 0 && (
        <EmptyState
          title="No PRN Medication Available"
          description="There are no PRN medications currently configured for this service user, but administration history is shown above for audit purposes."
          actionButton={
            <button
              className="btn btn__secondary mt-16"
              onClick={() =>
                navigate(`/field-shifts/${shiftId}/${serviceUserId}`)
              }
            >
              Go Back
            </button>
          }
        />
      )}
    </>
  );
};

export const FieldShiftPRN = React.memo(FieldShiftPRNComponent);
